import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  DecoratedCompanyPost,
  UndecoratedCompanyPost,
} from '@flemingmetrics/api-client';
import { filterByValue, mergeWithoutDuplicates } from '@/utils/arrays';
import { buildCompositeKey, Identifier } from '@/store/utils';
import { deletePost } from './postsSlice';

type CompanyPostsState = {
  byId: Record<string, UndecoratedCompanyPost>;
  byCompanyId: Record<string, string[]>;
  byPostId: Record<string, string>;
};

const initialState: CompanyPostsState = {
  byId: {},
  byCompanyId: {},
  byPostId: {},
};

export const companyPostsSlice = createSlice({
  name: 'companyPosts',
  initialState,
  reducers: {
    addCompanyPost: (state, action: PayloadAction<DecoratedCompanyPost>) => {
      return getNextState(state, [action.payload]);
    },
    setCompanyPosts: (state, action: PayloadAction<DecoratedCompanyPost[]>) => {
      return getNextState(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(deletePost, (state, action) => {
      const { postId } = action.payload;
      let byId = { ...state.byId };
      let byCompanyId = { ...state.byCompanyId };
      let byPostId = { ...state.byPostId };
      const key = byPostId[postId];
      const companyPost = byId[key];
      if (!companyPost) return state;
      if (byCompanyId[companyPost.companyId]) {
        byCompanyId[companyPost.companyId] = filterByValue(
          byCompanyId[companyPost.companyId],
          key
        );
      }
      delete byPostId[postId];
      delete byId[key];
      return { byId, byCompanyId, byPostId };
    });
    builder.addDefaultCase(() => {});
  },
});

function formatCompanyPost({
  post,
  ...companyPost
}: DecoratedCompanyPost): UndecoratedCompanyPost {
  return companyPost;
}

function getNextState(
  state: CompanyPostsState,
  companyPosts: DecoratedCompanyPost[]
) {
  const byId = { ...state.byId };
  const byCompanyId = { ...state.byCompanyId };
  const byPostId = { ...state.byPostId };
  companyPosts.forEach((cp) => {
    const { companyId, postId } = cp;
    const key = buildCompanyPostKey(companyId, postId);
    byId[key] = formatCompanyPost(cp);
    if (!byCompanyId[companyId]) byCompanyId[companyId] = [];
    byCompanyId[companyId] = mergeWithoutDuplicates(byCompanyId[companyId], [
      key,
    ]);
    byPostId[postId] = key;
  });
  return { byId, byCompanyId, byPostId };
}

export function buildCompanyPostKey(companyId: Identifier, postId: Identifier) {
  return buildCompositeKey(companyId, postId);
}

export const { addCompanyPost, setCompanyPosts } = companyPostsSlice.actions;

export default companyPostsSlice.reducer;
